import { z } from 'zod';
import { excursionValues } from './schemas';
import type { Excursion } from './schemas';

/** Practical facts shown on public pages; edited by the admin in «Экскурсии» (server/routes/excursions.ts). */
export type ExcursionFacts = {
  excursion: Excursion;
  price: string;
  duration: string;
  age: string;
  groupSize: string;
  meetingPoint: string;
  address: string;
  mapUrl: string;
  /** Hidden by the admin (e.g. out of season): off public lists, the booking form and the sitemap; its page shows a note. */
  hidden: boolean;
  /** Position on public lists, smaller first; set by the admin's ↑/↓ in «Экскурсии» (027_excursion_sort.sql). */
  sort: number;
};
export type ExcursionFactsMap = Record<Excursion, ExcursionFacts>;

/** True when the admin hid this offer from the public site; courses and missing facts count as visible. */
export function isExcursionHidden(facts: Partial<ExcursionFactsMap> | undefined, offer: string): boolean {
  return facts?.[offer as Excursion]?.hidden === true;
}

/** Items in the admin's excursion order (stable); missing facts use the defaults, non-excursions go last. */
export function orderByExcursion<T>(items: readonly T[], facts: Partial<ExcursionFactsMap> | undefined, id: (item: T) => string): T[] {
  const rank = (item: T) => (facts?.[id(item) as Excursion] ?? defaultExcursionFacts[id(item) as Excursion])?.sort ?? Number.MAX_SAFE_INTEGER;
  return [...items].sort((left, right) => rank(left) - rank(right));
}

/** Field limits shared by the zod schema, the admin form and the CHECK constraints of 008_excursion_details.sql. */
export const excursionFactLimits = { price: 120, duration: 80, age: 80, groupSize: 80, meetingPoint: 200, address: 200, mapUrl: 500 } as const;

/** Yandex Maps search link for an address in Krasnodar (no embedded maps or scripts). */
export function yandexMapsUrl(address: string): string {
  return `https://yandex.ru/maps/?text=${encodeURIComponent(`Краснодар, ${address}`)}`;
}

const placeholder = { price: 'Цена уточняется', unknown: 'Уточняется', meetOnAgreement: 'Место посадки сообщим при согласовании' } as const;

/**
 * Seed values (also inserted by 008_excursion_details.sql and 019_western_depot.sql — keep them in sync). Depot
 * addresses are from kttu.ru (/vtd-1, /ztd-1, /trldp-1); prices, durations and the rest are placeholders for the owner.
 */
export const defaultExcursionFacts: ExcursionFactsMap = {
  eastern_tram_depot: {
    excursion: 'eastern_tram_depot', price: placeholder.price, duration: 'около 1,5 часа', age: 'с 7 лет', groupSize: 'до 25 человек',
    meetingPoint: 'У проходной Восточного трамвайного депо', address: 'ул. Ставропольская, 202', mapUrl: yandexMapsUrl('ул. Ставропольская, 202'), hidden: false, sort: 10,
  },
  city_tram: {
    excursion: 'city_tram', price: placeholder.price, duration: 'около 1 часа', age: 'с 7 лет', groupSize: placeholder.unknown,
    meetingPoint: placeholder.meetOnAgreement, address: '', mapUrl: '', hidden: false, sort: 20,
  },
  trolleybus_depot_1: {
    excursion: 'trolleybus_depot_1', price: placeholder.price, duration: 'около 1,5 часа', age: 'с 7 лет', groupSize: 'до 25 человек',
    meetingPoint: 'У проходной Первого троллейбусного депо', address: 'ул. Дзержинского, 97', mapUrl: yandexMapsUrl('ул. Дзержинского, 97'), hidden: false, sort: 30,
  },
  western_tram_depot: {
    excursion: 'western_tram_depot', price: placeholder.unknown, duration: '1–1,5 часа', age: 'с 6 лет', groupSize: placeholder.unknown,
    meetingPoint: 'У проходной Западного трамвайного депо', address: 'ул. Славянская, 89', mapUrl: yandexMapsUrl('ул. Славянская, 89'), hidden: false, sort: 40,
  },
  city_tram_custom: {
    excursion: 'city_tram_custom', price: placeholder.price, duration: placeholder.unknown, age: 'с 7 лет', groupSize: placeholder.unknown,
    meetingPoint: placeholder.meetOnAgreement, address: '', mapUrl: '', hidden: false, sort: 50,
  },
  tram_karaoke: {
    excursion: 'tram_karaoke', price: placeholder.price, duration: placeholder.unknown, age: placeholder.unknown, groupSize: placeholder.unknown,
    meetingPoint: placeholder.meetOnAgreement, address: '', mapUrl: '', hidden: false, sort: 60,
  },
  botanical_walk: {
    excursion: 'botanical_walk', price: placeholder.price, duration: 'около 2 часов', age: 'с 7 лет', groupSize: 'до 25 человек',
    meetingPoint: 'Парк по выбору: Чистяковская роща, Солнечный остров, Ботанический сад', address: '', mapUrl: '', hidden: false, sort: 70,
  },
};

const text = (maximum: number, required: boolean) => z.string()
  .transform((value) => value.trim().replace(/\s+/g, ' '))
  .pipe(required ? z.string().min(1, 'Заполните поле').max(maximum, `Максимум ${maximum} символов`) : z.string().max(maximum, `Максимум ${maximum} символов`));

/** Admin edit payload (the excursion id comes from the URL). */
export const excursionFactsSchema = z.object({
  price: text(excursionFactLimits.price, true),
  duration: text(excursionFactLimits.duration, true),
  age: text(excursionFactLimits.age, true),
  groupSize: text(excursionFactLimits.groupSize, true),
  meetingPoint: text(excursionFactLimits.meetingPoint, true),
  address: text(excursionFactLimits.address, false),
  mapUrl: z.string().trim().max(excursionFactLimits.mapUrl, `Максимум ${excursionFactLimits.mapUrl} символов`).refine(
    (value) => value === '' || /^https:\/\/(?:yandex\.(?:ru|com)\/maps(?:[/?#]|$)|ya\.cc\/)[^\s"<>]*$/.test(value),
    'Укажите ссылку на Яндекс Карты (https://yandex.ru/maps/…) или оставьте поле пустым',
  ),
}).strict();

export type ExcursionFactsInput = z.input<typeof excursionFactsSchema>;

/** Admin hide/show payload (PATCH /api/admin/excursions/:excursion). */
export const excursionVisibilitySchema = z.object({ hidden: z.boolean({ errorMap: () => ({ message: 'Укажите, скрыть ли экскурсию' }) }) }).strict();

/** Admin reorder payload (PUT /api/admin/excursions/order): every excursion exactly once, first shown first. */
export const excursionOrderSchema = z.object({
  order: z.array(z.enum(excursionValues)).refine(
    (order) => order.length === excursionValues.length && new Set(order).size === order.length,
    'Передайте каждую экскурсию ровно один раз',
  ),
}).strict();
